(() => {
  if (customElements.get('clh-date-sort')) return;
  const compare = (a, b) => b.pinned - a.pinned || b.date - a.date || b.created - a.created || a.id.localeCompare(b.id);
  class ClhDateSort extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      this.container = this.closest('#ProductGridContainer');
      this.status = this.querySelector('[data-clh-sort-status]');
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 50);
      this.onHistory = () => this.renderPage(false);
      window.addEventListener('popstate', this.onHistory);
      this.load().catch(error => {
        if (error.name === 'AbortError') return;
        this.container?.removeAttribute('aria-busy');
        if (this.grid) this.grid.style.visibility = '';
        if (this.originalPagination) this.originalPagination.hidden = false;
        this.status.hidden = false;
        this.status.textContent = this.dataset.error;
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      window.removeEventListener('popstate', this.onHistory);
    }
    async fetchPage(page) {
      const url = new URL(window.location.href);
      url.searchParams.set('view', 'clh-sort-source');
      url.searchParams.set('page', String(page));
      url.searchParams.set('sort_by', 'created-descending');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      const response = await fetch(url, { credentials: 'same-origin', signal: this.controller.signal });
      if (!response.ok) throw new Error('Source request failed');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = document.querySelector('[data-clh-sort-source]');
      if (!root || Number(root.dataset.page) !== page) throw new Error('Incomplete sorting source');
      return {
        pages: Number(root.dataset.pages),
        count: Number(root.dataset.count),
        records: [...root.querySelectorAll('[data-clh-record]')].map(record => ({
          id: record.dataset.id,
          pinned: Number(record.dataset.pinned),
          date: Number(record.dataset.date),
          created: Number(record.dataset.created),
          card: record.querySelector('.product-card')
        }))
      };
    }
    async load() {
      this.grid = this.container.querySelector('motion-list.card-grid');
      if (!this.grid) return;
      this.originalPagination = this.container.querySelector('pagination-element, .pagination');
      this.promotions = [...this.grid.children]
        .filter(node => node.classList.contains('product-card--promo'))
        .map((node, order) => ({node, order, position: Math.max(1, Number(node.dataset.clhPromotionPosition) || 1)}))
        .sort((a, b) => a.position - b.position || a.order - b.order);
      this.status.hidden = false;
      this.status.textContent = this.dataset.loading;
      this.container.setAttribute('aria-busy', 'true');
      this.grid.style.visibility = 'hidden';
      if (this.originalPagination) this.originalPagination.hidden = true;
      const first = await this.fetchPage(1);
      if (!Number.isInteger(first.pages) || first.pages < 0 || first.pages > 100 || !Number.isInteger(first.count)) throw new Error('Invalid source size');
      const batches = [first];
      for (let start = 2; start <= first.pages; start += 3) {
        const pages = Array.from({length: Math.min(3, first.pages - start + 1)}, (_, index) => start + index);
        batches.push(...await Promise.all(pages.map(page => this.fetchPage(page))));
      }
      if (batches.some(batch => batch.count !== first.count || batch.pages !== first.pages)) throw new Error('Source changed during loading');
      const records = batches.flatMap(batch => batch.records);
      if (records.length !== first.count || new Set(records.map(record => record.id)).size !== records.length) throw new Error('Missing or duplicate records');
      this.records = records.filter(record => record.card).sort(compare);
      this.nav = document.createElement('nav');
      this.nav.className = 'clh-date-pagination flex flex-wrap items-center justify-center gap-3';
      this.nav.setAttribute('aria-label', this.dataset.previous + ' / ' + this.dataset.next);
      this.nav.addEventListener('click', event => {
        const link = event.target.closest('a[data-clh-page]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        history.pushState({}, '', link.href);
        this.renderPage(true);
      });
      this.container.append(this.nav);
      this.renderPage(false);
      this.container.removeAttribute('aria-busy');
      this.grid.style.visibility = '';
      this.status.hidden = true;
      this.dataset.ready = 'true';
    }
    renderPage(scroll) {
      if (!this.records) return;
      const totalPages = Math.max(1, Math.ceil(this.records.length / this.pageSize));
      const requested = Math.max(1, Number(new URL(window.location.href).searchParams.get('page')) || 1);
      const page = Math.min(totalPages, Math.floor(requested));
      if (requested !== page) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(page));
        history.replaceState({}, '', url);
      }
      const fragment = document.createDocumentFragment();
      for (const record of this.records.slice((page - 1) * this.pageSize, page * this.pageSize)) {
        const node = document.importNode(record.card, true);
        node.dataset.clhProductId = record.id;
        fragment.append(node);
      }
      const products = [...fragment.children];
      if (products.length) {
        for (const promotion of this.promotions) {
          if (promotion.position > products.length) continue;
          const node = promotion.node.cloneNode(true);
          for (const property of ['opacity', 'visibility', 'transform']) node.style.removeProperty(property);
          fragment.insertBefore(node, products[promotion.position - 1]);
        }
      }
      this.grid.replaceChildren(fragment);
      for (const state of this.container.querySelectorAll('.empty-state')) state.hidden = this.records.length > 0;
      this.nav.replaceChildren();
      const addLink = (target, label, current = false) => {
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(target));
        const link = document.createElement('a');
        link.href = url.href;
        link.dataset.clhPage = String(target);
        link.className = 'button button--secondary';
        link.textContent = label;
        if (current) link.setAttribute('aria-current', 'page');
        this.nav.append(link);
      };
      if (totalPages > 1) {
        if (page > 1) addLink(page - 1, this.dataset.previous);
        const numbers = [...new Set([1, ...Array.from({length:5}, (_, index) => page - 2 + index).filter(number => number >= 1 && number <= totalPages), totalPages])].sort((a,b) => a-b);
        let last = 0;
        for (const number of numbers) {
          if (last && number - last > 1) {
            const dots = document.createElement('span');
            dots.textContent = '…';
            this.nav.append(dots);
          }
          addLink(number, String(number), number === page);
          last = number;
        }
        if (page < totalPages) addLink(page + 1, this.dataset.next);
      }
      document.dispatchEvent(new CustomEvent('collection:reloaded'));
      if (scroll) this.container.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
    }
  }
  customElements.define('clh-date-sort', ClhDateSort);
})();
